import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Phone, Mail, MapPin, Clock, Send, Loader2, CheckCircle, Play, ChevronDown, MessageSquare, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const S = {
  fontFace: `
    @import url('https://fonts.googleapis.com/css2?family=Clash+Display:wght@500;600;700&family=Satoshi:wght@300;400;500;700&display=swap');
  `,

  keyframes: `
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(18px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes pinBounce {
      0%,100% { transform: rotate(-45deg) translateY(0); }
      50%      { transform: rotate(-45deg) translateY(-6px); }
    }
    @keyframes pulse2 {
      0%,100% { opacity: 1; }
      50%      { opacity: 0.35; }
    }
    .cu-fade  { animation: fadeUp 0.5s ease both; }
    .cu-fade1 { animation-delay: 0.08s; }
    .cu-fade2 { animation-delay: 0.16s; }
    .cu-fade3 { animation-delay: 0.24s; }
    .cu-info-card:hover { border-color: #ff5a00 !important; transform: translateY(-3px); }
    .cu-form-input:focus { border-color: #ff5a00 !important; background: #fff !important; }
    .cu-faq-item:hover   { border-color: #e0e0e0 !important; }
    .cu-chip:hover       { border-color: #ff5a00; color: #ff5a00; }
    .cu-submit:hover:not(:disabled) { background: #e04e00; transform: translateY(-1px); }
    .cu-submit:disabled  { opacity: 0.5; cursor: not-allowed; }
    .cu-wa-btn:hover     { background: #1fb855 !important; transform: translateY(-1px); }
    .cu-chat-btn:hover   { opacity: 0.88; }
    ::placeholder { color: #bbb !important; }

    /* ── RESPONSIVE BREAKPOINTS ── */

    /* Tablet: 861px–1024px — keep two cols but tighter */
    @media (max-width: 1024px) {
      .cu-contact-grid { gap: 14px !important; }
    }

    /* Tablet portrait & large mobile: collapse to single column */
    @media (max-width: 860px) {
      .cu-contact-grid { grid-template-columns: 1fr !important; }
      .cu-form-row     { grid-template-columns: 1fr 1fr !important; }
    }

    /* Mobile: ≤600px */
    @media (max-width: 600px) {
      .cu-hero-section  { padding: 64px 16px 52px !important; }
      .cu-hero-stats    { gap: 24px !important; }
      .cu-section-inner { padding-left: 16px !important; padding-right: 16px !important; }
      .cu-section-wrap  { padding: 44px 16px !important; }
      .cu-info-grid     { grid-template-columns: 1fr 1fr !important; gap: 10px !important; }
      .cu-form-row      { grid-template-columns: 1fr !important; }
      .cu-form-header   { padding: 20px 18px 16px !important; }
      .cu-form-body     { padding: 18px 18px 24px !important; }
      .cu-map-info      { padding: 18px 18px !important; }
      .cu-chat-cta      { padding: 24px 18px !important; flex-direction: column !important; align-items: stretch !important; }
      .cu-chat-cta-btn  { width: 100% !important; justify-content: center !important; }
      .cu-video-grid    { grid-template-columns: 1fr !important; }
      .cu-hours-strip   { gap: 16px !important; }
    }

    /* Small mobile: ≤420px */
    @media (max-width: 420px) {
      .cu-info-grid  { grid-template-columns: 1fr !important; }
      .cu-stat-num   { font-size: 22px !important; }
      .cu-map-frame  { height: 200px !important; }
      .cu-cat-row    { gap: 5px !important; }
      .cu-detail-val { font-size: 11px !important; }
    }
  `,

  page: { fontFamily: "'Satoshi', sans-serif", background: '#f5f4f0', minHeight: '100vh', color: '#1a1a1a' },

  hero: { position: 'relative', overflow: 'hidden', padding: '110px 24px 90px', textAlign: 'center', background: '#ffffff', borderBottom: '1px solid #e8e5e0' },
  heroGrid: { position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,90,0,0.05) 1px,transparent 1px),linear-gradient(90deg,rgba(255,90,0,0.05) 1px,transparent 1px)', backgroundSize: '56px 56px' },
  heroGlow: { position: 'absolute', top: '-80px', left: '50%', transform: 'translateX(-50%)', width: '600px', height: '360px', background: 'radial-gradient(ellipse,rgba(255,90,0,0.10) 0%,transparent 70%)', pointerEvents: 'none' },
  heroTag: { display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(255,90,0,0.08)', border: '1px solid rgba(255,90,0,0.25)', borderRadius: '100px', padding: '5px 16px', fontSize: '12px', fontWeight: 600, color: '#ff5a00', marginBottom: '22px', letterSpacing: '0.09em', textTransform: 'uppercase' },
  heroDot: { width: '7px', height: '7px', borderRadius: '50%', background: '#ff5a00', animation: 'pulse2 2s infinite' },
  heroTitle: { fontFamily: "'Clash Display', sans-serif", fontSize: 'clamp(30px,5.5vw,68px)', fontWeight: 700, lineHeight: 1.06, letterSpacing: '-0.03em', color: '#111', marginBottom: '16px' },
  heroAccent: { color: '#ff5a00' },
  heroSub: { fontSize: 'clamp(14px,2vw,17px)', color: '#888', maxWidth: '500px', margin: '0 auto 48px', lineHeight: 1.75 },
  statsBar: { display: 'flex', justifyContent: 'center', gap: '40px', flexWrap: 'wrap', position: 'relative', zIndex: 1 },
  stat:     { textAlign: 'center' },
  statNum:  { fontFamily: "'Clash Display', sans-serif", fontSize: '28px', fontWeight: 700, color: '#111', display: 'block' },
  statLabel:{ fontSize: '12px', color: '#aaa' },

  section:      { padding: '64px 24px' },
  sectionInner: { maxWidth: '1160px', margin: '0 auto' },
  sectionLabel: { fontSize: '11px', fontWeight: 700, color: '#ff5a00', letterSpacing: '0.13em', textTransform: 'uppercase', marginBottom: '10px' },
  sectionTitle: { fontFamily: "'Clash Display', sans-serif", fontSize: 'clamp(20px,3vw,34px)', fontWeight: 700, color: '#111', letterSpacing: '-0.02em', marginBottom: '14px' },
  sectionSub:   { fontSize: '14px', color: '#888', lineHeight: 1.75 },

  infoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '12px', marginBottom: '32px' },
  infoCard: { background: '#fff', border: '1.5px solid #ece9e4', borderRadius: '18px', padding: '20px', transition: 'border-color 0.2s,transform 0.2s', cursor: 'default' },
  infoIcon:      { width: '40px', height: '40px', borderRadius: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' },
  infoCardTitle: { fontFamily: "'Clash Display', sans-serif", fontSize: '13px', fontWeight: 600, color: '#111', marginBottom: '5px' },
  infoCardText:  { fontSize: '12px', color: '#888', lineHeight: 1.65 },

  /* Two-col: form + map — collapses via CSS class */
  contactGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '18px', alignItems: 'start' },

  formCard:   { background: '#fff', border: '1.5px solid #ece9e4', borderRadius: '22px', overflow: 'hidden' },
  formHeader: { padding: '28px 32px 22px', borderBottom: '1px solid #f0ede8' },
  formTitle:  { fontFamily: "'Clash Display', sans-serif", fontSize: '19px', fontWeight: 700, color: '#111', marginBottom: '4px' },
  formSub:    { fontSize: '13px', color: '#aaa' },
  formBody:   { padding: '24px 32px 32px' },

  formRow:   { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '5px', marginBottom: '10px' },
  formLabel: { fontSize: '10px', fontWeight: 700, color: '#aaa', letterSpacing: '0.08em', textTransform: 'uppercase' },
  formInput: { background: '#f8f7f5', border: '1.5px solid #ece9e4', borderRadius: '10px', padding: '10px 13px', fontSize: '14px', color: '#111', outline: 'none', transition: 'border-color 0.2s,background 0.2s', width: '100%', boxSizing: 'border-box', fontFamily: "'Satoshi',sans-serif" },

  catRow: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' },
  chip: (active) => ({ padding: '5px 12px', borderRadius: '100px', fontSize: '11px', fontWeight: 600, border: `1.5px solid ${active ? '#ff5a00' : '#e0ddd8'}`, background: active ? 'rgba(255,90,0,0.07)' : 'transparent', color: active ? '#ff5a00' : '#999', cursor: 'pointer', transition: 'all 0.15s', fontFamily: "'Satoshi',sans-serif" }),

  submitBtn: { width: '100%', background: '#ff5a00', border: 'none', borderRadius: '11px', padding: '13px 20px', fontSize: '14px', fontWeight: 700, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'background 0.15s,transform 0.12s', marginTop: '4px', fontFamily: "'Satoshi',sans-serif", letterSpacing: '0.02em' },

  successBox:    { textAlign: 'center', padding: '48px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' },
  successCircle: { width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(52,211,153,0.1)', border: '2px solid rgba(52,211,153,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  successTitle:  { fontFamily: "'Clash Display',sans-serif", fontSize: '20px', fontWeight: 700, color: '#111' },
  successSub:    { fontSize: '13px', color: '#888' },
  successBtn:    { marginTop: '4px', background: '#f5f4f0', border: '1.5px solid #e0ddd8', borderRadius: '10px', padding: '8px 20px', fontSize: '13px', color: '#555', cursor: 'pointer', fontFamily: "'Satoshi',sans-serif" },

  mapCard:   { background: '#fff', border: '1.5px solid #ece9e4', borderRadius: '22px', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  mapFrame:  { position: 'relative', height: '260px' },
  mapIframe: { width: '100%', height: '100%', border: 0, display: 'block', filter: 'saturate(0.7) brightness(1.02)' },
  mapPin:    { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-60%)', pointerEvents: 'none', zIndex: 3 },
  pinDot:    { width: '30px', height: '30px', background: '#ff5a00', borderRadius: '50% 50% 50% 0', transform: 'rotate(-45deg)', border: '3px solid #fff', boxShadow: '0 0 0 5px rgba(255,90,0,0.2)', animation: 'pinBounce 2s ease-in-out infinite' },
  mapInfo:     { padding: '20px 24px', borderTop: '1px solid #f0ede8' },
  mapLocName:  { fontFamily: "'Clash Display',sans-serif", fontSize: '15px', fontWeight: 700, color: '#111', marginBottom: '3px' },
  mapLocAddr:  { fontSize: '12px', color: '#888', lineHeight: 1.6, marginBottom: '16px' },
  detailList:  { display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' },
  detailItem:  { display: 'flex', alignItems: 'center', gap: '10px' },
  detailIcon:  { width: '34px', height: '34px', borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  detailLabel: { fontSize: '10px', fontWeight: 700, color: '#bbb', letterSpacing: '0.08em', textTransform: 'uppercase' },
  detailVal:   { fontSize: '12px', color: '#333', fontWeight: 500 },

  waBtn: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', background: '#25d366', border: 'none', borderRadius: '11px', padding: '11px 18px', fontSize: '13px', fontWeight: 700, color: '#fff', cursor: 'pointer', transition: 'background 0.15s,transform 0.12s', textDecoration: 'none', fontFamily: "'Satoshi',sans-serif", letterSpacing: '0.02em' },
  hoursStrip: { display: 'flex', gap: '20px', flexWrap: 'wrap', marginTop: '14px', paddingTop: '14px', borderTop: '1px solid #f0ede8' },
  hoursItem:  { display: 'flex', flexDirection: 'column' },
  hoursLabel: { fontSize: '10px', fontWeight: 700, color: '#bbb', textTransform: 'uppercase', letterSpacing: '0.08em' },
  hoursVal:   { fontSize: '12px', color: '#555', fontWeight: 500 },

  videoGrid:        { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: '16px', marginTop: '28px' },
  videoCard:        { background: '#fff', border: '1.5px solid #ece9e4', borderRadius: '18px', overflow: 'hidden', transition: 'border-color 0.2s' },
  videoPlaceholder: { height: '170px', background: 'linear-gradient(135deg,rgba(255,90,0,0.07),rgba(180,180,180,0.08))', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  videoPlayBtn:     { width: '50px', height: '50px', borderRadius: '50%', background: 'rgba(255,90,0,0.12)', border: '2px solid rgba(255,90,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  videoCardContent: { padding: '15px 18px' },
  videoCardTitle:   { fontFamily: "'Clash Display',sans-serif", fontSize: '13px', fontWeight: 600, color: '#111', marginBottom: '4px' },
  videoCardDesc:    { fontSize: '12px', color: '#aaa' },
  videoTag:         { display: 'inline-block', marginTop: '7px', fontSize: '10px', fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: '#ff5a00', background: 'rgba(255,90,0,0.07)', border: '1px solid rgba(255,90,0,0.2)', borderRadius: '5px', padding: '2px 7px' },

  faqList:    { display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '28px' },
  faqItem:    { background: '#fff', border: '1.5px solid #ece9e4', borderRadius: '14px', overflow: 'hidden', transition: 'border-color 0.2s' },
  faqQuestion:{ padding: '15px 18px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', fontWeight: 500, color: '#222', userSelect: 'none', gap: '12px' },
  faqAnswer:  { padding: '0 18px 15px', fontSize: '13px', color: '#888', lineHeight: 1.8 },

  chatCTA: { background: '#fff', border: '1.5px solid #ece9e4', borderRadius: '20px', padding: '36px 40px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '18px' },
  chatCTATitle: { fontFamily: "'Clash Display',sans-serif", fontSize: 'clamp(17px,2.5vw,20px)', fontWeight: 700, color: '#111', marginBottom: '5px' },
  chatCTASub:   { fontSize: '13px', color: '#888' },
  chatCTABtn:   { display: 'flex', alignItems: 'center', gap: '8px', background: '#ff5a00', border: 'none', borderRadius: '11px', padding: '12px 22px', fontSize: '13px', fontWeight: 700, color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'opacity 0.2s', fontFamily: "'Satoshi',sans-serif" },
};

const InfoCard = ({ icon, iconBg, iconColor, title, lines }) => (
  <div style={S.infoCard} className="cu-info-card">
    <div style={{ ...S.infoIcon, background: iconBg, color: iconColor }}>{icon}</div>
    <p style={S.infoCardTitle}>{title}</p>
    {lines.map((l, i) => <p key={i} style={S.infoCardText}>{l}</p>)}
  </div>
);

const FaqItem = ({ question, answer, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ ...S.faqItem, borderColor: open ? '#ff5a00' : undefined }} className="cu-faq-item" data-testid={`faq-item-${idx}`}>
      <div style={S.faqQuestion} onClick={() => setOpen(o => !o)}>
        <span>{question}</span>
        <ChevronDown size={14} style={{ color: '#bbb', transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none', flexShrink: 0 }} />
      </div>
      {open && <div style={S.faqAnswer}>{answer}</div>}
    </div>
  );
};

const ContactUs = () => {
  const [cities, setCities]             = useState([]);
  const [selectedCity, setSelectedCity] = useState('Belgaum');
  const [faqs, setFaqs]                 = useState([]);
  const [videos, setVideos]             = useState([]);
  const [loading, setLoading]           = useState(false);
  const [submitted, setSubmitted]       = useState(false);
  const [activeCategory, setActiveCategory] = useState('General');
  const [charCount, setCharCount]       = useState(0);
  const categories = ['General', 'Buying', 'Selling', 'Agent / Partner', 'Technical'];
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [citiesRes, faqsRes, videosRes] = await Promise.all([
        axios.get(`${API}/cities`),
        axios.get(`${API}/faqs`),
        axios.get(`${API}/videos?video_type=guide`),
      ]);
      setCities(citiesRes.data.cities);
      setFaqs(faqsRes.data.faqs);
      setVideos(videosRes.data.videos);
    } catch (err) { console.error('Failed to fetch data:', err); }
  };

  const handleCategorySelect = (cat) => {
    setActiveCategory(cat);
    setFormData(f => ({ ...f, subject: cat !== 'General' ? `${cat} Inquiry` : '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !formData.subject || !formData.message) {
      toast.error('Please fill all fields'); return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...formData, category: activeCategory });
      toast.success('Thank you! We will get back to you soon.');
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setCharCount(0);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit form');
    } finally { setLoading(false); }
  };

  const extractYouTubeId = (url) => {
    const match = url?.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/);
    return match ? match[1] : null;
  };

  const placeholderVideos = [
    { title: 'How to Buy a Vehicle',     description: 'Step-by-step guide for buyers',                tag: 'Buyer Guide' },
    { title: 'How to Sell Your Vehicle', description: "Complete seller's guide to listing & pricing", tag: 'Seller Guide' },
    { title: 'Agent Registration Guide', description: 'Become a verified X7 Autoz agent',             tag: 'For Agents' },
  ];

  return (
    <div style={S.page}>
      <style>{S.fontFace + S.keyframes}</style>
      <Header cities={cities} selectedCity={selectedCity} setSelectedCity={setSelectedCity} />

      {/* ── Hero ── */}
      <section style={S.hero} className="cu-hero-section">
        <div style={S.heroGrid} />
        <div style={S.heroGlow} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div className="cu-fade" style={S.heroTag}>
            <span style={S.heroDot} /> We respond within 2 hours
          </div>
          <h1 className="cu-fade cu-fade1" style={S.heroTitle}>
            Let's talk <span style={S.heroAccent}>vehicles</span>
          </h1>
          <p className="cu-fade cu-fade2" style={S.heroSub}>
            Whether you're buying your dream car or selling your current one, our team is ready to guide you every step of the way.
          </p>
          <div className="cu-fade cu-fade3 cu-hero-stats" style={S.statsBar}>
            {[
              { num: '10K+', label: 'Vehicles listed' },
              { num: '4.9★', label: 'Customer rating' },
              { num: '2hr',  label: 'Avg. response time' },
              { num: '50+',  label: 'Cities covered' },
            ].map(s => (
              <div key={s.label} style={S.stat}>
                <span className="cu-stat-num" style={S.statNum}>{s.num}</span>
                <span style={S.statLabel}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Contact section ── */}
      <section style={S.section} className="cu-section-wrap">
        <div style={S.sectionInner} className="cu-section-inner">
          <p style={S.sectionLabel}>Reach us</p>
          <h2 style={{ ...S.sectionTitle, marginBottom: '22px' }}>Multiple ways to connect</h2>

          {/* Info cards */}
          <div style={S.infoGrid} className="cu-info-grid">
            <InfoCard icon={<Phone size={18}/>}  iconBg="rgba(255,90,0,0.08)"    iconColor="#ff5a00" title="Phone"          lines={['+91 7204439777', '+91 7411298777']} />
            <InfoCard icon={<Mail size={18}/>}   iconBg="rgba(100,100,100,0.08)" iconColor="#555"    title="Email"          lines={['support@x7autoz.com', 'info@x7autoz.com']} />
            <InfoCard icon={<MapPin size={18}/>} iconBg="rgba(255,90,0,0.08)"    iconColor="#ff5a00" title="Address"        lines={['College Road, Near City Bus Stand', 'Belgaum, Karnataka – 590001']} />
            <InfoCard icon={<Clock size={18}/>}  iconBg="rgba(100,100,100,0.08)" iconColor="#555"    title="Business Hours" lines={['Mon – Sat: 9:00 AM – 7:00 PM', 'Sun: 10:00 AM – 4:00 PM']} />
          </div>

          {/* Form + Map — cu-contact-grid collapses to 1 col on tablet/mobile */}
          <div style={S.contactGrid} className="cu-contact-grid">

            {/* ── Form ── */}
            <div style={S.formCard}>
              <div style={S.formHeader} className="cu-form-header">
                <h3 style={S.formTitle}>Send us a message</h3>
                <p style={S.formSub}>We'll get back to you within 24 hours — usually much sooner.</p>
              </div>
              <div style={S.formBody} className="cu-form-body">
                {submitted ? (
                  <div style={S.successBox}>
                    <div style={S.successCircle}><CheckCircle size={28} color="#34d399" /></div>
                    <h3 style={S.successTitle}>Message received!</h3>
                    <p style={S.successSub}>Our team will reply within 24 hours.</p>
                    <button style={S.successBtn} onClick={() => setSubmitted(false)}>Send another message</button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <p style={{ ...S.formLabel, marginBottom: '9px' }}>What's this about?</p>
                    <div style={S.catRow} className="cu-cat-row">
                      {categories.map(cat => (
                        <button key={cat} type="button" className="cu-chip" style={S.chip(activeCategory === cat)} onClick={() => handleCategorySelect(cat)}>
                          {cat}
                        </button>
                      ))}
                    </div>

                    {/* Name + Email row */}
                    <div style={S.formRow} className="cu-form-row">
                      <div style={S.formGroup}>
                        <label style={S.formLabel}>Your name *</label>
                        <input className="cu-form-input" style={S.formInput} value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="John Doe" data-testid="contact-name-input" />
                      </div>
                      <div style={S.formGroup}>
                        <label style={S.formLabel}>Email *</label>
                        <input className="cu-form-input" style={S.formInput} type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} placeholder="john@example.com" data-testid="contact-email-input" />
                      </div>
                    </div>

                    {/* Phone + Subject row */}
                    <div style={S.formRow} className="cu-form-row">
                      <div style={S.formGroup}>
                        <label style={S.formLabel}>Phone *</label>
                        <input className="cu-form-input" style={S.formInput} value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} placeholder="+91 98765 43210" data-testid="contact-phone-input" />
                      </div>
                      <div style={S.formGroup}>
                        <label style={S.formLabel}>Subject *</label>
                        <input className="cu-form-input" style={S.formInput} value={formData.subject} onChange={e => setFormData({ ...formData, subject: e.target.value })} placeholder="How can we help?" data-testid="contact-subject-input" />
                      </div>
                    </div>

                    <div style={S.formGroup}>
                      <label style={S.formLabel}>Message * <span style={{ color: '#ccc', fontWeight: 400 }}>({500 - charCount} left)</span></label>
                      <textarea
                        className="cu-form-input"
                        style={{ ...S.formInput, resize: 'vertical', minHeight: '110px', lineHeight: 1.7 }}
                        value={formData.message}
                        maxLength={500}
                        onChange={e => { setFormData({ ...formData, message: e.target.value }); setCharCount(e.target.value.length); }}
                        placeholder="Tell us how we can help you..."
                        data-testid="contact-message-input"
                      />
                    </div>

                    <button type="submit" className="cu-submit" style={S.submitBtn} disabled={loading} data-testid="contact-submit-btn">
                      {loading ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={16} />}
                      {loading ? 'Sending…' : 'Send message'}
                    </button>
                  </form>
                )}
              </div>
            </div>

            {/* ── Map + info panel ── */}
            <div style={S.mapCard}>
              <div style={S.mapFrame} className="cu-map-frame">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d122398.53427080963!2d74.43477535!3d15.8497003!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bbf65fe2c728e0d%3A0xecafd21b0f152f64!2sBelgaum%2C%20Karnataka!5e0!3m2!1sen!2sin!4v1679900000000!5m2!1sen!2sin"
                  style={S.mapIframe}
                  allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="X7 Autoz Location"
                />
                <div style={S.mapPin}><div style={S.pinDot} /></div>
              </div>

              <div style={S.mapInfo} className="cu-map-info">
                <p style={S.mapLocName}>X7 Autoz — Belgaum</p>
                <p style={S.mapLocAddr}>Jay Nagar, Hindalga Road<br />Belgaum, Karnataka – 590001</p>

                <div style={S.detailList}>
                  <div style={S.detailItem}>
                    <div style={{ ...S.detailIcon, background: 'rgba(255,90,0,0.08)', color: '#ff5a00' }}><Phone size={16} /></div>
                    <div>
                      <p style={S.detailLabel}>Phone</p>
                      <p className="cu-detail-val" style={S.detailVal}>+91 7411298777 · +91 7204439777</p>
                    </div>
                  </div>
                  <div style={S.detailItem}>
                    <div style={{ ...S.detailIcon, background: 'rgba(100,100,100,0.07)', color: '#555' }}><Mail size={16} /></div>
                    <div>
                      <p style={S.detailLabel}>Email</p>
                      <p className="cu-detail-val" style={S.detailVal}>support@x7autoz.com</p>
                    </div>
                  </div>
                </div>

                <a className="cu-wa-btn" style={S.waBtn} href="https://wa.me/917411298777" target="_blank" rel="noreferrer">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  Chat on WhatsApp
                </a>

                <div style={S.hoursStrip} className="cu-hours-strip">
                  <div style={S.hoursItem}><span style={S.hoursLabel}>Mon – Sat</span><span style={S.hoursVal}>9:00 AM – 7:00 PM</span></div>
                  <div style={S.hoursItem}><span style={S.hoursLabel}>Sunday</span><span style={S.hoursVal}>10:00 AM – 4:00 PM</span></div>
                </div>
              </div>
            </div>

          </div>{/* end cu-contact-grid */}
        </div>
      </section>

      {/* ── Guide Videos ── */}
      <section style={{ ...S.section, background: '#fff', borderTop: '1px solid #ece9e4', borderBottom: '1px solid #ece9e4', padding: '52px 24px 60px' }} className="cu-section-wrap">
        <div style={S.sectionInner} className="cu-section-inner">
          <p style={S.sectionLabel}>Learn how it works</p>
          <h2 style={S.sectionTitle}>Video guides</h2>
          <p style={S.sectionSub}>Watch our quick walkthroughs for buying, selling, and becoming a verified agent.</p>
          <div style={S.videoGrid} className="cu-video-grid">
            {(videos.length > 0 ? videos.slice(0, 3) : placeholderVideos).map((video, idx) => {
              const videoId = video.youtube_url ? extractYouTubeId(video.youtube_url) : null;
              return (
                <div key={video.id || idx} style={S.videoCard} data-testid={`guide-video-${idx}`}>
                  {videoId ? (
                    <iframe width="100%" height="170" src={`https://www.youtube.com/embed/${videoId}`} title={video.title} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{ display: 'block' }} />
                  ) : (
                    <div style={S.videoPlaceholder}>
                      <div style={S.videoPlayBtn}><Play size={20} color="#ff5a00" style={{ marginLeft: '2px' }} /></div>
                    </div>
                  )}
                  <div style={S.videoCardContent}>
                    <p style={S.videoCardTitle}>{video.title}</p>
                    {video.description && <p style={S.videoCardDesc}>{video.description}</p>}
                    {!videoId && <span style={S.videoTag}>{video.tag || 'Coming soon'}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── FAQs ── */}
      <section style={S.section} className="cu-section-wrap">
        <div style={S.sectionInner} className="cu-section-inner">
          <p style={S.sectionLabel}>Got questions?</p>
          <h2 style={S.sectionTitle}>Frequently asked questions</h2>
          <p style={S.sectionSub}>Quick answers to what our customers ask most.</p>
          <div style={S.faqList}>
            {(faqs.length > 0 ? faqs : [
              { question: 'How do I list my vehicle for sale?',               answer: 'Create a free account, click "Sell Your Vehicle", fill in the details and upload photos. Your listing goes live within minutes after our quick review.' },
              { question: 'Is there a fee for buyers?',                       answer: 'No — browsing and contacting sellers is completely free for buyers. We only charge a small listing fee to sellers.' },
              { question: 'How are agents verified?',                         answer: 'Agents submit their credentials through the Agent Registration form. Our team verifies identity and experience within 2–3 business days.' },
              { question: 'Can I negotiate the price directly with the seller?', answer: 'Yes. Our platform connects you directly with sellers or their agents so you can negotiate openly and transparently.' },
              { question: 'What cities do you operate in?',                   answer: 'We currently cover 50+ cities across Karnataka, Maharashtra, and Goa — with more regions being added every month.' },
            ]).map((faq, idx) => (
              <FaqItem key={faq.id || idx} question={faq.question} answer={faq.answer} idx={idx} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Chat CTA ── */}
      <section style={{ padding: '0 24px 60px' }}>
        <div style={{ maxWidth: '1160px', margin: '0 auto' }}>
          <div style={S.chatCTA} className="cu-chat-cta">
            <div>
              <p style={S.chatCTATitle}>Still have questions?</p>
              <p style={S.chatCTASub}>Chat with our support team — we're online Mon–Sat, 9 AM to 7 PM.</p>
            </div>
            <button className="cu-chat-btn cu-chat-cta-btn" style={S.chatCTABtn} onClick={() => toast.info('Live chat coming soon!')}>
              <MessageSquare size={16} /> Start live chat <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactUs;